import React, { useState, useEffect } from 'react';
import { Card, Badge, Button, Alert, Spinner, Row, Col } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { aiService, LiveAlert } from '../../../services/aiService';

const LiveAlerts: React.FC = () => {
  const [alerts, setAlerts] = useState<LiveAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadAlerts();
    // 每10秒刷新一次實時警報
    const interval = setInterval(loadAlerts, 10000);
    return () => clearInterval(interval);
  }, []);

  const loadAlerts = async () => {
    try {
      const data = await aiService.getLiveAlerts();
      // 確保 data 是數組
      if (Array.isArray(data)) {
        setAlerts(data);
      } else {
        console.warn('獲取的警報數據不是數組:', data);
        setAlerts([]);
      }
      setError(null);
    } catch (err) {
      setError('無法載入實時警報');
      console.error('載入實時警報失敗:', err);
      // 設置空數組以防止運行時錯誤
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async (alertId: string) => {
    try {
      await aiService.acknowledgeDetection(alertId);
      toast.success('警報已確認處理');
      await loadAlerts();
    } catch (err) {
      toast.error('確認警報失敗');
      console.error('確認警報失敗:', err);
    }
  };

  const handleFalsePositive = async (alertId: string) => {
    try {
      await aiService.markAsFalsePositive(alertId);
      toast.success('已標記為誤報');
      await loadAlerts();
    } catch (err) {
      toast.error('標記誤報失敗');
      console.error('標記誤報失敗:', err);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'danger';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      case 'low':
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  const getSeverityText = (severity: string) => {
    switch (severity) {
      case 'critical':
        return '嚴重';
      case 'high':
        return '高';
      case 'medium':
        return '中';
      case 'low':
        return '低';
      default:
        return severity;
    }
  };

  const getTypeText = (type: string) => {
    const typeNames: Record<string, string> = {
      abnormal_behavior: '異常行為',
      fire_smoke: '火災煙霧',
      fall_detection: '跌倒檢測',
      vehicle_collision: '車輛碰撞'
    };
    return typeNames[type] || type;
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) {
      return '剛剛';
    } else if (diffMins < 60) {
      return `${diffMins}分鐘前`;
    } else {
      return date.toLocaleTimeString('zh-TW', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    }
  };

  if (loading) {
    return (
      <Card>
        <Card.Header>
          <h5 className="mb-0">
            <i className="fas fa-exclamation-triangle me-2"></i>
            實時警報
          </h5>
        </Card.Header>
        <Card.Body className="text-center py-4">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">載入中...</span>
          </Spinner>
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <Card.Header>
          <h5 className="mb-0">
            <i className="fas fa-exclamation-triangle me-2"></i>
            實時警報
          </h5>
        </Card.Header>
        <Card.Body>
          <Alert variant="danger">
            <i className="fas fa-exclamation-triangle me-2"></i>
            {error}
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card>
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h5 className="mb-0">
          <i className="fas fa-exclamation-triangle me-2"></i>
          實時警報
          {alerts.length > 0 && (
            <Badge bg="danger" className="ms-2">
              {alerts.length}
            </Badge>
          )}
        </h5>
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={loadAlerts}
          disabled={loading}
        >
          <i className="fas fa-sync-alt me-1"></i>
          刷新
        </Button>
      </Card.Header>
      <Card.Body>
        {!Array.isArray(alerts) || alerts.length === 0 ? (
          <div className="text-center py-4 text-muted">
            <i className="fas fa-shield-alt display-4 mb-3"></i>
            <p className="mb-0">目前沒有活躍警報</p>
            <small>系統運行正常</small>
          </div>
        ) : (
          <div className="detection-timeline">
            {alerts.map((alert) => (
              <div key={alert._id} className={`alert-card card mb-3 severity-${alert.severity}`}>
                <Card.Body>
                  <Row className="align-items-center">
                    <Col md={8}>
                      <div className="d-flex align-items-start mb-2">
                        <Badge 
                          bg={getSeverityColor(alert.severity)} 
                          className="me-2 detection-type-badge"
                        >
                          {getSeverityText(alert.severity)}
                        </Badge>
                        <Badge 
                          bg="secondary" 
                          className="detection-type-badge"
                        >
                          {getTypeText(alert.type)}
                        </Badge>
                      </div>
                      <h6 className="mb-1">{alert.description}</h6>
                      <div className="text-muted small">
                        <i className="fas fa-map-marker-alt me-1"></i>
                        {alert.location}
                        <span className="mx-2">•</span>
                        <i className="fas fa-video me-1"></i>
                        {alert.cameraName}
                        <span className="mx-2">•</span>
                        <i className="fas fa-clock me-1"></i>
                        {formatTime(alert.timestamp)}
                      </div>
                      <div className="mt-2">
                        <small className="text-muted">置信度: </small>
                        <div className="confidence-bar d-inline-block" style={{ width: '100px' }}>
                          <div 
                            className={`confidence-fill ${
                              alert.confidence >= 0.8 ? 'high' : 
                              alert.confidence >= 0.6 ? 'medium' : 'low'
                            }`}
                            style={{ width: `${alert.confidence * 100}%` }}
                          ></div>
                        </div>
                        <small className="ms-2">{(alert.confidence * 100).toFixed(1)}%</small>
                      </div>
                    </Col>
                    <Col md={4} className="text-end">
                      <div className="d-flex flex-column gap-2">
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() => handleAcknowledge(alert._id)}
                        >
                          <i className="fas fa-check me-1"></i>
                          確認處理
                        </Button>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => handleFalsePositive(alert._id)}
                        >
                          <i className="fas fa-times me-1"></i>
                          標記誤報
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </div>
            ))}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default LiveAlerts;